import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useEmailSendsStats } from '@/client/modules/email/hooks/useEmailSends'
import { useEmailCampaigns } from '@/client/modules/email/hooks/useEmailCampaigns'
import { Mail, Send, CheckCircle, AlertCircle, ChevronRight, Clock } from 'lucide-react'
import { formatRelativeTime } from '@/lib/utils'

/**
 * EmailMetricsWidget Component
 *
 * Displays email campaign and sending statistics on the dashboard.
 * Shows:
 * - Total emails sent
 * - Success/fail rates
 * - Recent sends
 * - Draft/scheduled campaigns
 */
export function EmailMetricsWidget() {
  const { data: stats, isLoading: statsLoading, error: statsError } = useEmailSendsStats()
  const { data: campaigns, isLoading: campaignsLoading } = useEmailCampaigns({ limit: 5, offset: 0 })

  const isLoading = statsLoading || campaignsLoading

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">Email Activity</CardTitle>
            </div>
          </div>
          <CardDescription>Campaign and sending stats</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32" />
        </CardContent>
      </Card>
    )
  }

  if (statsError) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Email Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load email stats</p>
        </CardContent>
      </Card>
    )
  }

  const emailStats = stats?.stats || { total: 0, sent: 0, failed: 0, pending: 0, bounced: 0 }
  const recentSends = stats?.recentSends || []
  const campaignList = campaigns || []

  // Calculate success rate
  const successRate =
    emailStats.sent > 0
      ? Math.round(((emailStats.sent - emailStats.failed - emailStats.bounced) / emailStats.sent) * 100)
      : 0

  // Count campaigns by status
  const draftCampaigns = campaignList.filter((c) => c.status === 'draft').length
  const scheduledCampaigns = campaignList.filter((c) => c.status === 'scheduled').length
  const sentCampaigns = campaignList.filter((c) => c.status === 'sent').length

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Email Activity</CardTitle>
          </div>
          <Link
            to="/dashboard/email"
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            Manage
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <CardDescription>Campaign and sending stats</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Stats summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Send className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-lg font-bold">{emailStats.total}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Total Sent</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                <span className="text-lg font-bold">{successRate}%</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Success Rate</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
                <span className="text-lg font-bold">{emailStats.pending}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Pending</p>
            </div>
          </div>

          {/* Campaign status badges */}
          {(draftCampaigns > 0 || scheduledCampaigns > 0 || sentCampaigns > 0) && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {draftCampaigns > 0 && (
                <Badge variant="outline" className="text-xs">
                  {draftCampaigns} draft{draftCampaigns !== 1 && 's'}
                </Badge>
              )}
              {scheduledCampaigns > 0 && (
                <Badge variant="secondary" className="text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  {scheduledCampaigns} scheduled
                </Badge>
              )}
              {sentCampaigns > 0 && (
                <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  {sentCampaigns} sent
                </Badge>
              )}
            </div>
          )}

          {/* Recent sends */}
          {recentSends.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground">Recent Sends</p>
              {recentSends.slice(0, 3).map((send) => (
                <div
                  key={send.id}
                  className="flex items-center justify-between text-xs py-1"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        send.status === 'sent'
                          ? 'bg-green-500'
                          : send.status === 'failed'
                            ? 'bg-red-500'
                            : 'bg-yellow-500'
                      }`}
                    />
                    <span className="truncate">
                      {send.recipientName || send.recipientEmail}
                    </span>
                  </div>
                  <span className="text-muted-foreground shrink-0 ml-2">
                    {formatRelativeTime(send.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {emailStats.total === 0 && campaignList.length === 0 && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No email activity yet</p>
              <Link
                to="/dashboard/email"
                className="text-xs text-primary hover:underline mt-1 inline-block"
              >
                Create your first campaign
              </Link>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
